import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  TextField,
  useTheme,
  Tooltip,
  Skeleton,
  Avatar,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Pagination
} from '@mui/material';

import { Search, Clear } from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddCitiesModal from 'src/sections/AddCitiesModal';
import CityViewModal from 'src/sections/CityViewModal';
import { addCities, getCitiesTable, deleteCity } from 'src/ApiServices';

export default function Cities() {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  // Pagination states
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });

  const fetchCities = async (currentPage = 1, search = '') => {
    try {
      setLoading(true);
      const response = await getCitiesTable(currentPage, search);
      setCities(response?.data && Array.isArray(response.data) ? response.data : []);
      setPagination(
        response.pagination || {
          current_page: 1,
          per_page: 20,
          total: response.data?.length || 0,
          last_page: 1,
          has_more_pages: false
        }
      );
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
      toast.error('Failed to load cities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities(page, searchQuery);
  }, [page]);

  const handleModalSubmit = async (formData) => {
    try {
      const response = await addCities(formData);

      toast.success(
        response?.message || `City ${selectedCity ? 'updated' : 'added'} successfully!`
      );
      fetchCities(page);
      handleModalClose();
    } catch (error) {
      console.error('Error adding/updating city:', error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          `Failed to ${selectedCity ? 'update' : 'add'} city`
      );
    }
  };

  const handleViewClick = (city) => {
    setSelectedCity(city);
    setViewModalOpen(true);
  };

  const handleEditClick = (city) => {
    setSelectedCity(city);
    setModalOpen(true);
  };

  const handleDeleteClick = (city) => {
    setSelectedCity(city);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCity) return;

    try {
      setDeleteLoading(true);
      await deleteCity(selectedCity.id);
      toast.success('City deleted successfully!');
      fetchCities(page);
      setDeleteModalOpen(false);
      setSelectedCity(null);
    } catch (error) {
      console.error('Failed to delete city:', error);
      toast.error(error.response?.data?.message || 'Failed to delete city!');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedCity(null);
  };

  const handleAddClick = () => {
    setSelectedCity(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCity(null);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchCities(page);
  };

  const handleSearchSubmit = useCallback(async () => {
    setPage(1); // Reset to first page on new search
    await fetchCities(1, searchQuery); // Pass current searchQuery
  }, [searchQuery]);

  const handleClearSearch = useCallback(async () => {
    setSearchQuery('');
    setPage(1);
    await fetchCities(1, '');
  }, []);

  const tableHeaders = ['Image', 'State', 'City', 'Published', 'Actions'];

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={100} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={120} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={80} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={60} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2, textAlign: 'center' }}>
          <Skeleton variant="circular" width={40} height={40} />
        </TableCell>
      </TableRow>
    ));

  const renderNoRecordsFound = () => {
    return (
      <TableRow>
        <TableCell
          colSpan={tableHeaders.length}
          sx={{
            textAlign: 'center',
            py: 8,
            borderBottom: 'none'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                color: palette.text.secondary,
                fontFamily: 'poppinsMedium',
                mb: 1
              }}
            >
              No Cities Found
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: palette.grey[600],
                fontFamily: 'poppinsRegular',
                maxWidth: 400,
                textAlign: 'center'
              }}
            >
              No cities available. Add a new city to get started.
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Page title={`Cities | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, mt: 2, pb: 2, pl: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Manage Cities
          </Typography>
          <Button
            variant="contained"
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              '&:hover': { background: palette.primary.dark },
              '&:disabled': { background: palette.grey[400] }
            }}
            onClick={handleAddClick}
            startIcon={<AddIcon />}
          >
            Add Cities
          </Button>
        </Box>

        {/* Search Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={10}>
            <TextField
              variant="outlined"
              placeholder="Search States or Cities..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearchSubmit}
                fullWidth
                sx={{
                  height: 56,
                  fontSize: '14px',
                  fontFamily: 'poppinsMedium'
                }}
                startIcon={<Search />}
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Card>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                minWidth: 650,
                '& .MuiTableCell-head': {
                  backgroundColor: palette.background.paper,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsSemiBold',
                  fontSize: '14px',
                  borderBottom: `2px solid ${palette.divider}`,
                  py: 2
                },
                '& .MuiTableCell-body': {
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  fontSize: '14px',
                  borderBottom: `1px solid ${palette.divider}`,
                  py: 2
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        whiteSpace: 'nowrap',
                        px: 2,
                        ...(header === 'Actions' && { width: 150, textAlign: 'center' })
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? renderSkeletonRows()
                  : cities.length > 0
                  ? cities.map((city) => (
                      <TableRow
                        key={city.id}
                        sx={{
                          '&:hover': { backgroundColor: palette.action.hover },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ px: 2 }}>
                          <Avatar src={city.image} alt={city.city} sx={{ width: 40, height: 40 }} />
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>{city?.state?.name}</TableCell>
                        <TableCell sx={{ px: 2 }}>{city?.city?.name}</TableCell>
                        <TableCell sx={{ px: 2 }}>{city.published_at}</TableCell>
                        {/* <TableCell sx={{ px: 2 }}>
                          {city.status?.charAt(0).toUpperCase() + city.status?.slice(1)}
                        </TableCell> */}
                        <TableCell sx={{ px: 2, textAlign: 'center' }}>
                          <Tooltip title="View City">
                            <IconButton
                              size="small"
                              onClick={() => handleViewClick(city)}
                              sx={{ color: palette.info.main, mr: 1 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit City">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(city)}
                              sx={{ color: palette.warning.main, mr: 1 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete City">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(city)}
                              sx={{ color: palette.error.main }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  : renderNoRecordsFound()}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Only show pagination if there are records */}
        {!loading && cities.length > 0 && pagination.total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        <AddCitiesModal
          open={modalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSubmit}
          city={selectedCity}
        />

        <CityViewModal
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedCity(null);
          }}
          city={selectedCity}
        />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1
            }
          }}
        >
          <DialogTitle
            id="delete-dialog-title"
            sx={{
              py: 3,
              pb: 2,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <WarningAmberIcon
              sx={{
                fontSize: 60,
                color: palette.warning.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              component="span"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText
              id="delete-dialog-description"
              sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}
            >
              Are you sure you want to delete{' '}
              <strong>
                {selectedCity?.city?.name ||
                  selectedCity?.city_name ||
                  selectedCity?.name ||
                  'this city'}
              </strong>
              ?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                '&:hover': {
                  borderColor: palette.grey[600],
                  backgroundColor: palette.action.hover
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                '&:hover': {
                  backgroundColor: palette.error.dark
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
              startIcon={
                deleteLoading ? (
                  <Skeleton variant="circular" width={20} height={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
//
